import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface SectionCardProps {
  title: string;
  /** Grå metatekst ved siden av tittelen («4 prosjekter · 1,9 MNOK»). */
  meta?: ReactNode;
  /** Knapper og lenker til høyre i topplinjen. */
  actions?: ReactNode;
  /**
   * Tomtilstand: når satt og `children` er tom, kollapser kortet til én linje med
   * denne teksten som meta. Alltid en forklaring og neste handling, aldri bare «Ingen data».
   */
  empty?: string;
  /** Kompakt topplinje (sidekolonnen). */
  compact?: boolean;
  /** Tilgjengelig navn på seksjonen; default = title. */
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
}

/**
 * Ett visuelt språk for alle seksjoner på kundekort v3: kort med ramme, én
 * topplinje (tittel, meta, handlinger) og innhold under. En tom seksjon er bare
 * topplinjen, så en kunde uten data ikke blir en side full av tomtekster.
 */
export default function SectionCard({ title, meta, actions, empty, compact = false, ariaLabel, className, children }: SectionCardProps) {
  const isEmpty = children == null || children === false;
  return (
    <section aria-label={ariaLabel ?? title} className={cn("border-[1.5px] border-foreground bg-card", className)}>
      <div className={cn("flex flex-wrap items-center justify-between gap-x-4 gap-y-2", compact ? "px-4 py-3" : "px-5 py-3.5")}>
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className={cn("font-display font-semibold tracking-tight", compact ? "text-base" : "text-lg")}>{title}</h2>
          {(isEmpty && empty ? empty : meta) && (
            <span className="text-sm text-muted-foreground">{isEmpty && empty ? empty : meta}</span>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {!isEmpty && <div className="border-t border-border">{children}</div>}
    </section>
  );
}
